import * as React from "react"
import { KeyRound, Lock, Settings2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { useBookingT } from "@/hooks/use-translations"
import type { SetupField } from "@/features/integrations/api/types"

type Props = {
  fields: SetupField[]
}

export function SetupRequirementsSection({ fields }: Props) {
  const t = useBookingT()
  if (fields.length === 0) return null

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {t("integrations_setup_required_title", "What you'll need")}
          </h2>
          <p className="text-sm text-neutral-600">
            {t(
              "integrations_setup_required_desc",
              "These fields will be requested when you connect this integration",
            )}
          </p>
        </div>

        <ul className="space-y-3">
          {fields.map((field) => (
            <li key={field.key} className="flex items-start gap-3 rounded-md border border-neutral-200 bg-neutral-50 p-3">
              <div className="mt-0.5 shrink-0">
                {field.secret ? (
                  <Lock className="size-4 text-amber-600" />
                ) : field.kind === "credential" ? (
                  <KeyRound className="size-4 text-brand-500" />
                ) : (
                  <Settings2 className="size-4 text-neutral-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-medium text-neutral-900">{field.title}</span>
                  {field.secret && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                      {t("integrations_setup_secret", "Secret")}
                    </span>
                  )}
                </div>
                {field.description && (
                  <p className="mt-0.5 text-sm text-neutral-700">{field.description}</p>
                )}
                {field.example && (
                  <p className="mt-1 font-mono text-xs text-neutral-600">
                    {t("integrations_setup_example", "Example")}: {field.example}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
